import { z } from 'zod';

import { CurrencyCodeSchema } from './common.js';

/**
 * AppConfig is the runtime contract between server and client.
 *
 * The server reads this from the `app_config`, `theme_settings`, and
 * `feature_flags` tables and serves it at GET /api/v1/config. The client
 * fetches it once at boot and provides it via AppConfigProvider.
 *
 * Nothing in this schema should ever be hardcoded in either app. If a piece
 * of UI needs to vary (brand color, locale, currency list, feature gate),
 * it gets added here, modeled in the DB, and consumed from this provider.
 *
 * Versioning: bump `version` whenever the response shape changes in a
 * breaking way. The client warns on mismatch.
 */

export const APP_CONFIG_VERSION = 1 as const;

/**
 * Branding URLs may be either fully-qualified (`https://cdn.example.com/logo.svg`)
 * - typical when assets live on a CDN - or same-origin paths (`/logo.svg`),
 * which is what dev / single-origin deployments use. Accept both.
 */
const AssetUrlSchema = z
  .string()
  .min(1)
  .refine((v) => /^https?:\/\//i.test(v) || v.startsWith('/'), {
    message: 'Must be an absolute URL or a same-origin path starting with /',
  });

export const BrandingSchema = z.object({
  appName: z.string().min(1),
  shortName: z.string().min(1),
  logoUrl: AssetUrlSchema.nullable(),
  /** Square brand mark, shown where space is tight (e.g. the collapsed sidebar). */
  iconUrl: AssetUrlSchema.nullable(),
  /**
   * Optional solid background painted behind the logo on light surfaces (e.g.
   * the expanded sidebar wordmark). Use it when the logo artwork is white or
   * transparent and would otherwise be invisible. `null` = transparent.
   * Any CSS colour string; the admin branding form writes a hex value.
   */
  logoBgColor: z.string().nullable(),
  logoDarkUrl: AssetUrlSchema.nullable(),
  faviconUrl: AssetUrlSchema.nullable(),
  supportEmail: z.string().email(),
  supportPhone: z.string().nullable(),
  legalEntityName: z.string(),
  legalEntityUrl: z.string().url().nullable(),
});

export const ThemeColorsSchema = z.object({
  background: z.string(), // HSL components, e.g. "0 0% 100%"
  foreground: z.string(),
  primary: z.string(),
  primaryForeground: z.string(),
  secondary: z.string(),
  secondaryForeground: z.string(),
  accent: z.string(),
  accentForeground: z.string(),
  muted: z.string(),
  mutedForeground: z.string(),
  destructive: z.string(),
  destructiveForeground: z.string(),
  success: z.string(),
  warning: z.string(),
  border: z.string(),
  ring: z.string(),
});

export const ThemeSchema = z.object({
  colors: z.object({
    light: ThemeColorsSchema,
    dark: ThemeColorsSchema,
  }),
  radius: z.string(), // e.g. "0.5rem"
  fonts: z.object({
    sans: z.string(),
    heading: z.string(),
    mono: z.string(),
  }),
  defaultMode: z.enum(['light', 'dark', 'system']),
});

export const LocalizationSchema = z.object({
  defaultLocale: z.string(), // e.g. "en-IN"
  defaultTimezone: z.string(), // e.g. "Asia/Kolkata"
  defaultCurrency: CurrencyCodeSchema,
  supportedCurrencies: z.array(CurrencyCodeSchema).min(1),
  dateFormat: z.string(), // e.g. "dd MMM yyyy"
  timeFormat: z.enum(['12h', '24h']),
});

export const FeatureFlagsSchema = z.record(z.string(), z.boolean());

export const LimitsSchema = z.object({
  maxItineraryDays: z.number().int().positive(),
  maxLeadAttachmentSizeMb: z.number().int().positive(),
  maxQuotationVersions: z.number().int().positive(),
  passwordMinLength: z.number().int().min(8),
});

/**
 * Tax configuration for itinerary quotes. GST applies to DOMESTIC trips;
 * TCS applies to INTERNATIONAL trips (mutually exclusive - see the lead's
 * leadCategory). GST is a chosen slab (5 / 12 / 18); TCS is a free string
 * (it changes by regulation - e.g. 5 -> 3 for travel), parsed as a percent.
 */
export const TaxSchema = z.object({
  gstRatePercent: z.number().nonnegative(),
  tcsRatePercent: z.string(),
});

/**
 * Lead-workflow toggles (2026-08-10). Exposed to the client so the
 * LeadStatusChanger + follow-up + publish flows can decide whether to
 * include the travel-intent fields in their client-side readiness check
 * without racing the server.
 */
export const LeadsConfigSchema = z.object({
  /** When true, travel-intent fields are required at mark-active AND
   *  re-checked on next action for already-active leads. Mirrored from
   *  app_config `leads.travelIntent.enforce`. */
  travelIntentEnforce: z.boolean(),
});

export const AppConfigSchema = z.object({
  version: z.literal(APP_CONFIG_VERSION),
  environment: z.enum(['development', 'staging', 'production']),
  branding: BrandingSchema,
  theme: ThemeSchema,
  localization: LocalizationSchema,
  features: FeatureFlagsSchema,
  limits: LimitsSchema,
  tax: TaxSchema,
  leads: LeadsConfigSchema,
  generatedAt: z.string().datetime(),
});

export type Branding = z.infer<typeof BrandingSchema>;
export type ThemeColors = z.infer<typeof ThemeColorsSchema>;
export type Theme = z.infer<typeof ThemeSchema>;
export type Localization = z.infer<typeof LocalizationSchema>;
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;
export type Limits = z.infer<typeof LimitsSchema>;
export type Tax = z.infer<typeof TaxSchema>;
export type LeadsConfig = z.infer<typeof LeadsConfigSchema>;
export type AppConfig = z.infer<typeof AppConfigSchema>;
